import { Link, useLocation, useNavigate } from "react-router-dom";
import { calculateDurationObject, capitalizeFirstLetter, getMeetingSentimentDisplay, useTimeLeft, validateDate } from "../../utils/utils";
import { Box, Button, IconButton, InputBase, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import logo7G from "../../assets/common/logo7G.svg";
import dayjs from "dayjs";
import '../../../src/styles/common.css'
import ShareIcon from "../../assets/meeting/share-icon.svg";
import "../../styles/interactions.css";
import { GlobalContext } from "../../context/GlobalContext";
import { useContext, useEffect, useMemo, useState } from "react";
const initialSort = { sort_field: "start_time", sort_order: "desc" };
import Ask7GIcon from "../../assets/common/ask7g-icon.svg";
import SelectedAsk7GIcon from "../../assets/common/ask7g-selected.svg";
import EllipsedTooltip from "../Common/EllipsedTooltip";
import EditIcon from "../../assets/common/edit-icon.svg";
import EnterIcon from "../../assets/common/enter-arrow-icon.svg";
import CancelIcon from "../../assets/common/discard_icon.svg";
import MomLinkIcon from "../../assets/action-items/File-link-icon.svg";
import ColumnFilter from "../Common/customFilter.tsx";

const clippingCellProps = {
  sx: {
    minWidth: 0,
    overflow: "hidden",
    fontSize: "13px",
  },
};

const UpcomingMeetingCountdown = ({
  timeLeft,
  botStatus,
  scheduledTooltip,
}: {
  timeLeft: { days: number; hours: number; minutes: number };
  botStatus?: string | null;
  scheduledTooltip: string;
}) => {
  return (
    <Box className="meeting-sentiment__upcoming">
      <AccessTimeIcon sx={{ fontSize: "15px" }} />
      <Typography className="meeting-sentiment__time">
        {timeLeft.days > 0
          ? `Starts in ${timeLeft.days}d ${timeLeft.hours}h ${timeLeft.minutes}m`
          : `Starts in ${timeLeft.hours}h ${timeLeft.minutes}m`}
      </Typography>
      {botStatus === "Scheduled" && (
        <Tooltip title={scheduledTooltip} arrow>
          <Box className="meeting-sentiment__logo-box">
            <img
              src={logo7G}
              alt="7thGear"
              className="meeting-sentiment__logo"
            />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

const MeetingSentimentCell = ({
  cell,
  row,
  scheduledTooltip,
  noSentimentInRatingColumnEnabled,
}: {
  cell: any;
  row: any;
  scheduledTooltip: string;
  noSentimentInRatingColumnEnabled?: boolean;
}) => {
  const { featureMap } = useContext(GlobalContext);
  const hideSentimentInRatingColumn =
    noSentimentInRatingColumnEnabled ??
    featureMap?.no_sentiment_in_rating_column === true;
  const sentiment = cell?.getValue();
  const startTime = useMemo(
    () => new Date(row.original?.start_time),
    [row.original?.start_time]
  );
  const timeLeft = useTimeLeft(startTime);
  const botStatus = row.original?.botStatus;

  const display = getMeetingSentimentDisplay({
    startTime,
    duration: row.original?.duration,
    endTime: row.original?.end_time,
    sentiment,
    isPreviousAsset: row.original?.isPreviousAsset,
    isPresentAsset: row.original?.isPresentAsset,
    botStatus,
    noSentimentInRatingColumnEnabled: hideSentimentInRatingColumn,
  });

  if (display.type === "upcoming") {
    return (
      <UpcomingMeetingCountdown
        timeLeft={timeLeft}
        botStatus={botStatus}
        scheduledTooltip={scheduledTooltip}
      />
    );
  }

  if (display.type === "inProgress") {
    return (
      <Box className="meeting-sentiment__inprogress">
        <AccessTimeIcon sx={{ fontSize: "15px" }} />
        <Typography className="meeting-sentiment__time">
          In Progress
        </Typography>
      </Box>
    );
  }

  if (display.type === "rating") {
    return (
      <Box
        className="meeting-sentiment__box"
        sx={{ backgroundColor: display.color }}
        title={display.sentiment}
      />
    );
  }

  if (display.type === "botStatus") {
    return (
      <EllipsedTooltip
        tooltipContent={display.message}
        className="meeting-sentiment__no-recording"
        maxWidth="140px"
      >
        {display.message}
      </EllipsedTooltip>
    );
  }

  return (
    <Typography className="meeting-sentiment__no-recording">
      {display.type === "recordingPresent" ? "Recording Present" : "No Recording"}
    </Typography>
  );
};

const displayTaggedLabel = (value: unknown): string => {
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) {
    return value
      .map((item) => displayTaggedLabel(item))
      .filter(Boolean)
      .join(", ");
  }
  if (value && typeof value === "object" && "name" in value) {
    const name = (value as { name?: unknown }).name;
    return typeof name === "string" ? name.trim() : "";
  }
  return "";
};

const getColumns = (
  selectedTab?: string,
  onSaveMeetingTitle?: (row: any, newTitle: string) => void,
  columnFilters?: any,
  updateColumnFilter?: (key: string, value: any) => void,
  accountOptions?: any[],
  openFilterColumn?: string | null,
  setOpenFilterColumn?: (key: string | null) => void,
  isMeetingsPage?: boolean,
  opportunityOptions?: any[],
  noSentimentInRatingColumnEnabled?: boolean,
) => {
  return [
    {
      accessorKey: "title",
      header: "Interaction title",
      Cell: ({ cell, row }: { cell: any; row: any }) => {
        const attendees = row.original.attendees;
        let allEmails: any[] = [];
        if (attendees && Object.keys(attendees).length > 0) {
          for (const key in attendees) {
            allEmails = allEmails.concat(attendees[key]);
          }
        }
        const isExternal = row.original.is_external;
        const theme = useTheme();
        const location = useLocation();
        const is_team_meeting_id = location.state?.is_team_meeting_id ?? false;
        // const clientDomains = row?.original?.clientDomains;
        const endTime = row?.original?.end_time;
        const isMeetingOver = endTime && new Date(endTime) < new Date();
        const navigate = useNavigate();
        const params = new URLSearchParams();
        if (row.original?.selectedTab) params.set("st", row.original.selectedTab);
        if (row.original?.isClientInteraction) params.set("ci", "1");
        if (is_team_meeting_id === true || row.original?.isTeamMeeting) params.set("ti", "1");
        if (isMeetingOver) params.set("imo", "1");
        const { featureMap } = useContext(GlobalContext);
        const isAsk7GFeatureEnabled = featureMap?.idp_ask_7g === true;
        const [hover, setHover] = useState(false);
        const canEditTitle = selectedTab === "myInteraction" && row.original?.isAdhocInteraction === true;
        const [isEditing, setIsEditing] = useState(false);
        const [titleHover, setTitleHover] = useState(false);
        const [value, setValue] = useState((cell.getValue() as string) ?? "");

        useEffect(() => {
          setValue(((row.original?.title ?? cell.getValue()) as string) ?? "");
        }, [row.original?.title]);
    
        const stateObject = {
          is_team_meeting_id: is_team_meeting_id === true ? true : row.original.isTeamMeeting ?? false,
          selectedTab: row.original.selectedTab,
          startEndTime: row.original.startEndTime,
          newUserTeamData: row.original.newUserTeamData,
          parent_page_identifier: row.original.parent_page_identifier,
          ParentPageIdentifier: row.original.ParentPageIdentifier,
          sharedWithMe: row.original.sharedWithMe,
          isMeetingOver: isMeetingOver,
          sharedByMe: row.original.sharedByMe,
          isClientInteraction: row.original?.isClientInteraction,
          clientDomain: row.original?.clientDomain,
          externalFilter: row.original?.externalFilter,
          pageSize: row.original?.pageSize,
          pageNumber: row.original?.pageNumber,
          tableFilters: row.original?.tableFilters,
          selectedNps: row.original?.selectedNps,
          isSlideOverOpen: row.original?.isSlideOverOpen,
          slideOverParent: row.original?.slideOverParent,
          activeStat: row.original?.activeStat,
          userEmail: row.original?.userEmail,
          profileOrgId: row.original?.profileOrgId,
          appliedFilters: row.original?.appliedFilters,
          customDateRange: row.original?.customDateRange,
          clickeduserEmail: row.original?.clickeduserEmail,
          filterState: row.original?.filterState,
          sort: row.original?.sort,
          userName: row.original?.userName,
          userDetails: row.original?.userDetails,
          orgId: row.original?.orgId,
          subOrgId: row.original?.subOrgId,
          clientName: row?.original?.clientName,
          activeView: row.original.activeView,
          accountViewState: row.original.accountViewState,
          opportunityNavigationState: row.original?.opportunityNavigationState,
        };
        const handleInsightClick = (isAsk7GEnabled = false) => {
          navigate(`/meeting/${row.id}?${params.toString()}`, {
            state: { 
              ...stateObject,
              isAsk7GEnabled, // Add flag only when icon clicked
            },
          });
        };

        const handleSaveTitle = () => {
          const trimmedValue = value.trim();
          if (!trimmedValue) return;
          setIsEditing(false);
          setValue(trimmedValue);
          if (trimmedValue !== (((cell.getValue() as string) ?? "").trim())) {
            onSaveMeetingTitle?.(row, trimmedValue);
          }
        };

        const handleCancelEdit = () => {
          setIsEditing(false);
          setValue(((row.original?.title ?? cell.getValue()) as string) ?? "");
        };

        return (
          <Box className="interaction-title-cell flex flex-row gap-1 justify-between items-start w-full">
              <Box
                onMouseEnter={() => setTitleHover(true)}
                onMouseLeave={() => setTitleHover(false)}
                className={`interaction-title-wrapper ${
                  canEditTitle && !isEditing
                    ? "interaction-title-wrapper--editable"
                    : ""
                }`}
              >
                {canEditTitle && isEditing ? (
                  <Box className="interaction-title-edit-container">
                    <InputBase
                      value={value}
                      autoFocus
                      onChange={(e) => setValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveTitle();
                      }}
                      className="interaction-title-input"
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSaveTitle();
                      }}
                      disabled={  value.trim() === "" || value.trim() === (((cell.getValue() as string) ?? "").trim())}
                      className="interaction-title-save-button"
                    >
                      Save
                      <img src={EnterIcon} alt="enter" className="interaction-title-cancel-icon" />
                    </Button>
                    <Tooltip title="Cancel" placement="top" arrow>
                      <img
                        src={CancelIcon}
                        alt="cancel"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancelEdit();
                        }}
                        style={{ width: "20px", height: "20px", cursor: "pointer" }}
                      />
                    </Tooltip>
                  </Box>
                ) : (
                  <Box className="interaction-title-display"
                  >
                    <EllipsedTooltip tooltipContent={cell.getValue() as string} maxWidth="100%" >
                      <Link
                        color="primary"
                        to={`/meeting/${row.id}?${params.toString()}`}
                        state={stateObject}
                        className="interaction-title-link truncate-table-cell-ellipsis"
                      >
                        {cell.getValue() as string}
                      </Link>
                    </EllipsedTooltip>
                    {row.original.sentimentSummary && isAsk7GFeatureEnabled && (
                      <Tooltip title ="Ask 7G" placement="top" arrow>
                      <img
                        onClick={() => handleInsightClick(isAsk7GFeatureEnabled)}
                        src={hover? SelectedAsk7GIcon : Ask7GIcon}
                        onMouseEnter={() => setHover(true)}
                        onMouseLeave={() => setHover(false)}
                        className="interaction-title-ask7g-icon"
                        />
                    </Tooltip>
                  )}
                  </Box>
                )}
                {canEditTitle && !isEditing && (
                  <Tooltip title="Edit" placement="top">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsEditing(true);
                      }}
                      className={`interaction-title-edit-btn ${
                        titleHover
                          ? "interaction-title-edit-btn--visible"
                          : "interaction-title-edit-btn--hidden"
                      }`}
                    >
                      <img src={EditIcon} alt="Edit" width={14} height={14} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            <Box
              className="flex flex-row justify-center items-center gap-2"
              sx={{
                flexGrow: 0,
                flexShrink: 0,
                flexBasis: "auto",
                minWidth: "70px",
                maxWidth: "70px",
                display: "inline-block",
                textAlign: "center",
                background: "#ffffff",
                borderRadius: "16px",
                border: `1px ${
                  isExternal ? theme.palette.brand.main : "#585858"
                }  solid`,
                padding: "4px 8px",
                [theme.breakpoints.down("lg")]: {
                  padding: "2px 6px",
                },
                marginLeft: "auto",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color: isExternal ? theme.palette.brand.main : "#222222",
                  fontWeight: "600",
                  wordWrap: "break-word",
                }}
              >
                {isExternal ? "External" : "Internal"}
              </Typography>
            </Box>
          </Box>
        );
      },
      enableColumnFilter: false,
      enableSorting: false,
      grow: true,
      minSize: 350,
      size: 350,
    },
    {
      accessorKey: "momDocLink",
      header: "MoM Link",
      Header: () => (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            marginBottom: "8px"
          }}
        >
          MoM Link
        </Box>
      ),
      enableColumnFilter: false,
      enableSorting: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      grow: true,
      minSize: 100,
      size: 100,
      Cell: ({ cell }: { cell: any }) => {
        const momDocLink = cell.getValue() as string | null | undefined;
        if (!momDocLink) {
          return <Box className="icon-cell">-</Box>;
        }

        return (
          <Box className="icon-cell">
          <a
            href={momDocLink}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open MOM"
          >
            <img
              src={MomLinkIcon}
              alt=""
            />
          </a>
        </Box>
        );
      },
    },
    {
      accessorKey: "sharedDate",
      header: "Shared Date",
      filterVariant: "date",
      filterFn: "lessThan",
      sortingFn: "datetime",
      Cell: ({ cell }: { cell: any }) => {
        if(cell.getValue() === null || cell.getValue() === undefined){
          return <span>-</span>
        }        
        return (
          <div>
            <span>{validateDate(cell.getValue(), "MMM DD, YYYY")}</span>
          </div>
        );
      },
      grow: true,
      minSize: 150,
      size: 150,
    },
    {
      accessorKey: "start_time",
      header: (selectedTab === "sharedWithMe" || selectedTab === "sharedByMe") ? "Interaction Date": "Date",
      filterVariant: "date",
      filterFn: "lessThan",
      sortingFn: "datetime",
      Cell: ({ cell }: { cell: any }) => {
        if(cell.getValue() === null || cell.getValue() === undefined){
          return <span>-</span>
        }        
        return (
          <div>
            <span>{validateDate(cell.getValue(), "MMM DD, YYYY hh:mm A")}</span>
          </div>
        );
      },
      grow: true,
      minSize: 200,
      size: 200,
    },
    {
      accessorKey: "account",
      header: "Account",
      ...(isMeetingsPage && accountOptions && updateColumnFilter && {
        Header: () => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              padding: "1px",
              backgroundColor: openFilterColumn === "account" ? "#F2F2F2" : "inherit",
              marginBottom: "8px"
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "15px",
                lineHeight: "18px",
                color:
                  Array.isArray(columnFilters?.account) &&
                  columnFilters?.account.length > 0
                    ? "#6036d6"
                    : "inherit",
              }}
            >
              Account
            </Typography>
            <ColumnFilter
              currentValue={columnFilters?.account ?? []}
              onApply={(val) => updateColumnFilter("account", val)}
              options={accountOptions}
              type="multi"
              showSections={{
                search: true,
                sort: false,
              }}
              columnKey="account"
              onOpen={() => setOpenFilterColumn?.("account")}
              onClose={() => setOpenFilterColumn?.(null)}
              isActive={
                Array.isArray(columnFilters?.account) &&
                columnFilters?.account.length > 0
              }
              parent_page="meetings-page"
            />
          </Box>
        ),
      }),
      enableColumnFilter: false,
      muiTableBodyCellProps: {
      ...clippingCellProps,
      sx: {
        ...clippingCellProps?.sx,
        textAlign: "left",
        padding: "10px"
      },
    },
      muiTableHeadCellProps: {
        sx:{
          justifyContent: "center",
          alignItems: "center",
          borderBottom: "1px solid #7A7A8C",
          "&[data-pinned='true']:before": {
              boxShadow: "none !important",
              backgroundColor: "#FFFFFF !important",
              borderRight:"1px solid #C2C2CA",
          },
          "&[data-pinned='true']:hover": {
            backgroundColor: "#F2F2F4 !important",
            cursor: "pointer",
          },
           padding: "5px",
        }
      },
      Cell: ({ cell }: { cell: any }) => {
        const rawValue = cell.getValue();
        const accounts: string[] = Array.from(
          new Set(
            (Array.isArray(rawValue)
              ? rawValue
              : typeof rawValue === "string" && rawValue.trim()
              ? [rawValue.trim()]
              : []
            )
              .map((item: any) => capitalizeFirstLetter(displayTaggedLabel(item)))
              .filter(Boolean)
          )
        );

        if (accounts.length === 0) return <span>-</span>;

        if (accounts.length === 1) {
          return (
            <div className="interaction-account-single">
              <EllipsedTooltip tooltipContent={accounts[0]} maxWidth="100%">
                <span className="truncate-table-cell-ellipsis">{accounts[0]}</span>
              </EllipsedTooltip>
            </div>
          );
        }

        return (
          <Tooltip
            title={
              <div className="interaction-account-tooltip-list">
                {accounts.map((acc, index) => (
                  <span key={index}>{acc}</span>
                ))}
              </div>
            }
            arrow
            placement="top"
          >
            <div className="interaction-account-cell">
              <span className="truncate-table-cell-ellipsis interaction-account-text">
                {accounts[0]}
              </span>
              <span className="interaction-account-count">
                +{accounts.length - 1}
              </span>
            </div>
          </Tooltip>
        );
      },
      grow: true,
      minSize: 140,
      size: 150,
      enableSorting: false,
    },
    {
      accessorKey: "opportunityName",
      header: "Opportunity Name",
      ...(isMeetingsPage && opportunityOptions && updateColumnFilter && {
        Header: () => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              padding: "1px",
              backgroundColor: openFilterColumn === "opportunityName" ? "#F2F2F2" : "inherit",
              marginBottom: "8px"
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "15px",
                lineHeight: "18px",
                color:
                  Array.isArray(columnFilters?.opportunityName) &&
                  columnFilters?.opportunityName.length > 0
                    ? "#6036d6"
                    : "inherit",
              }}
            >
              Opportunity Name
            </Typography>
            <ColumnFilter
              currentValue={columnFilters?.opportunityName ?? []}
              onApply={(val) => updateColumnFilter("opportunityName", val)}
              options={opportunityOptions}
              type="multi"
              showSections={{
                search: true,
                sort: false,
              }}
              columnKey="opportunityName"
              hideUntilSearchThreshold={5}
              onOpen={() => setOpenFilterColumn?.("opportunityName")}
              onClose={() => setOpenFilterColumn?.(null)}
              isActive={
                Array.isArray(columnFilters?.opportunityName) &&
                columnFilters?.opportunityName.length > 0
              }
              parent_page="meetings-page"
            />
          </Box>
        ),
      }),
      enableColumnFilter: false,
      muiTableBodyCellProps: clippingCellProps,
      muiTableHeadCellProps: {
        sx:{
          justifyContent: "center",
          alignItems: "center",
          borderBottom: "1px solid #7A7A8C",
          "&[data-pinned='true']:before": {
              boxShadow: "none !important",
              backgroundColor: "#FFFFFF !important",
              borderRight:"1px solid #C2C2CA",
          },
          "&[data-pinned='true']:hover": {
            backgroundColor: "#F2F2F4 !important",
            cursor: "pointer",
          },
           padding: "5px",
        }
      },
      Cell: ({ cell }: { cell: any }) => {
        const rawValue = cell.getValue();
        const opportunities: string[] = Array.from(
          new Set(
            (Array.isArray(rawValue)
              ? rawValue
              : typeof rawValue === "string" && rawValue.trim()
              ? [rawValue.trim()]
              : []
            )
              .map((item: any) => capitalizeFirstLetter(displayTaggedLabel(item)))
              .filter(Boolean)
          )
        );

        if (opportunities.length === 0) return <span>-</span>;

        if (opportunities.length === 1) {
          return (
            <div className="interaction-account-single">
              <EllipsedTooltip tooltipContent={opportunities[0]} maxWidth="100%">
                <span className="truncate-table-cell-ellipsis">{opportunities[0]}</span>
              </EllipsedTooltip>
            </div>
          );
        }

        return (
          <Tooltip
            title={
              <div className="interaction-account-tooltip-list">
                {opportunities.map((opp, index) => (
                  <span key={index}>{opp}</span>
                ))}
              </div>
            }
            arrow
            placement="top"
          >
            <div className="interaction-account-cell">
              <span className="truncate-table-cell-ellipsis interaction-account-text">
                {opportunities[0]}
              </span>
              <span className="interaction-account-count">
                +{opportunities.length - 1}
              </span>
            </div>
          </Tooltip>
        );
      },
      grow: true,
      minSize: 170,
      size: 170,
      enableSorting: false,
    },
    {
      accessorKey: "duration",
      header: "Scheduled Duration",
      enableColumnFilter: false,
      Cell: ({ cell }: { cell: any }) => {
        const timeSpend = calculateDurationObject(
          cell.getValue() as number
        ) || {
          days: 0,
          hours: 0,
          minutes: 0,
        };
        if(cell.getValue() === null || cell.getValue() === undefined || cell.getValue() === 0){
          return <span>-</span>
        }
        return (
          <span>
            {timeSpend.days > 0 &&
              `${timeSpend.days} ${timeSpend.days === 1 ? "day" : "days"}`}
            {timeSpend.hours > 0 &&
              ` ${timeSpend.hours} ${timeSpend.hours === 1 ? "hr" : "hrs"}`}
            {timeSpend.minutes > 0 && ` ${timeSpend.minutes} mins`}
          </span>
        );
      },
      grow: true,
      minSize: 170,
      size: 170,
      enableSorting: false,
    },
    {
      accessorKey: "actual_duration",
      header: "Actual Duration",
      enableSorting: false,
      size: 100,
      grow: true,
      minSize: 150,
      maxSize: 150,
      Cell: ({cell, row }: {cell: any; row: any}) => {
        const value = cell.getValue() as number | null | undefined;
        const isPresentAsset = row.original?.isPresentAsset;
        if (!isPresentAsset) {
          return <span>-</span>;
        }

    if (value === null || value === undefined) {
      return <span>-</span>;
    }
    const totalSeconds = Math.round(value);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    let formatted = "";

    if (days > 0) {
      formatted = `${days} ${days === 1 ? "day" : "days"}`;
      if (hours > 0) formatted += ` ${hours} ${hours === 1 ? "hr" : "hrs"}`;
      if (minutes > 0) formatted += ` ${minutes} mins`;
    } else if (hours > 0) {
      formatted = `${hours} ${hours === 1 ? "hr" : "hrs"}`;
      if (minutes > 0) formatted += ` ${minutes} mins`;
    } else if (minutes > 0) {
      formatted = `${minutes} mins`;
    } else {
      formatted = `${seconds} secs`;
    }

    return <span>{formatted}</span>;
  },
    },
    {
      accessorKey: "is_manager",
      header: "Manager Present",
      enableColumnFilter: false,
      enableSorting: false,
      minSize: 150,
      size: 150,
      Cell: ({ cell }: { cell: any }) => {
        const isManager = cell.getValue();
        const displayValue = isManager === true ? "Yes" : "No";
        return <Typography>{displayValue}</Typography>;
      },
    },
    {
      accessorKey: "meeting_sentiment",
      header: "Rating",
      Cell: ({ cell, row }: { cell: any; row: any }) => (
        <MeetingSentimentCell
          cell={cell}
          row={row}
          scheduledTooltip="Analyzer Scheduled"
          noSentimentInRatingColumnEnabled={noSentimentInRatingColumnEnabled}
        />
      ),

        enableSorting: false,
        grow: true,
        minSize: 160,
        size: 160,
      },

    {
      accessorKey: "abstractiveScore",
      header: "Rating",
      Cell: ({ cell, row }: { cell: any; row: any }) => (
        <MeetingSentimentCell
          cell={cell}
          row={row}
          scheduledTooltip="Bot Scheduled"
          noSentimentInRatingColumnEnabled={noSentimentInRatingColumnEnabled}
        />
      ),
      enableSorting: false,
      grow: true,
      minSize: 160,
      size: 160,
    },
    {
      accessorKey: "rating_source",
      header: "Rating Source",
      Cell: ({ cell }: { cell: any }) => {
        if(cell.getValue() === null || cell.getValue() === undefined){
          return <span>-</span>
        }        
        const rowData = cell.row.original; // Get the entire row data
        let ratingSource = rowData.rating_source;
        if (ratingSource === "AiBased") {
          ratingSource = "AI based";
        } else if (ratingSource === "RuleBased") {
          ratingSource = "Rule based";
        } else {
          ratingSource = ratingSource || "NA"; // Fallback value
        }
        return (
          <Typography sx={{ fontSize: "13px" }}>{ratingSource}</Typography>
        );
      },
      enableColumnFilter: false,
      enableSorting: false,
      grow: true,
      minSize: 160,
      size: 120,
    },
    {
      accessorKey: "talkTrack",
      header: "Talk Ratio",
      enableSorting: false,
      size: 100,
      minSize: 200,
      maxSize: 300,
      grow: true,
      Cell: ({ row }: { row: any }) => {
        const duration = row.original.actualDuration || 1;
        const selfTime = row.original.selfTalkTime || 0;
        if (duration === null || duration === 0 || selfTime === 0) {
          return <span>-</span>;
        }
        const percentage = Math.min((selfTime / duration) * 100, 100).toFixed(1);
        return (
          <span>
            <span style={{ fontWeight: 500 }}>{percentage}%</span>{" "}
            <span style={{ color:"#6F6F7F" }}>({selfTime}s)</span>
          </span>
        );
      }
    },
    {
      id: "shareIcon",
      header: "Share",
      enableColumnFilter: false,
      enableSorting: false,
      grow: true,
      minSize: 60,
      size: 60,
      Cell: ({ row }: { row: any }) => {
        const {
          start_time: startTime,
          id: interactionId,
          showShareIcon,
          setSelectedInteractionIds,
          setOpenDialog,
        } = row?.original;

        if (!showShareIcon) return null;

        const isFuture = dayjs(startTime).isAfter(dayjs());
        const isPreviousAsset = row.original?.isPreviousAsset;
        const isPresentAsset = row.original?.isPresentAsset;
        const disableShare = (!isPreviousAsset && !isPresentAsset) || isFuture;
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              opacity: disableShare ? 0.4 : 1,
              pointerEvents: disableShare ? "none" : "auto",
              cursor: "pointer",
              marginLeft: "10px",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedInteractionIds([interactionId]);
              setOpenDialog(true);
            }}
          >
            <Tooltip title="Share" arrow>
              <img src={ShareIcon} alt="Share" style={{height: "24px", width: "24px"}} />
            </Tooltip>
          </Box>
        );
      },
      muiTableBodyCellProps: {
        sx: {
          padding: 0,
        },
      },
    },
  ];
};

export { getColumns, initialSort };
